/**
 * Keşif wizard — step model and validation, deliberately UI-free.
 *
 * Kept out of the components so the "can this step advance?" question has one
 * answer that is readable without reading JSX, mirroring `domain/wizard.ts`
 * for the debate setup wizard.
 *
 * Validation here is a UX affordance, NOT a security boundary. The backend
 * re-validates every field (`CrawlPolicy` is `extra="forbid"` with its own
 * authorisation gates), so the worst a bug here can do is let the user submit
 * something that comes back as a 422.
 */
import {
  CRAWL_LIMITS,
  CRAWL_POLICY_DEFAULTS,
  type CrawlPolicy,
} from "../../domain/kesif";
import type { PreflightResult } from "../../transport/crawlClient";

export type WizardStep = 1 | 2 | 3 | 4 | 5;

export const WIZARD_STEPS: readonly { step: WizardStep; title: string; hint: string }[] =
  [
    { step: 1, title: "Hedef", hint: "Hangi adresler taranacak?" },
    { step: 2, title: "Kapsam", hint: "Nereye kadar gidilecek?" },
    { step: 3, title: "Nezaket", hint: "Hangi hızda ve hangi kurallarla?" },
    { step: 4, title: "Çıkarım", hint: "Ne toplanacak ve nasıl işlenecek?" },
    { step: 5, title: "Özet", hint: "Onaylayıp başlat." },
  ];

export interface SeedEntry {
  /**
   * Stable across edits and removals. Array position is NOT an identity here:
   * an in-flight preflight probe resolves after the user may have deleted a
   * row above it, and an index-keyed write would land on the wrong seed.
   */
  id: string;
  /** Exactly what the user typed — never rewritten under the cursor. */
  raw: string;
  /** Server-normalised probe result; `null` until the debounce fires. */
  probe: PreflightResult | null;
  checking: boolean;
}

let seedSeq = 0;

export function newSeed(): SeedEntry {
  seedSeq += 1;
  return { id: `seed-${seedSeq}`, raw: "", probe: null, checking: false };
}

export interface WizardDraft {
  name: string;
  seeds: SeedEntry[];
  policy: CrawlPolicy;
  /** `false` creates the job in `queued` for a dry-run inspection first. */
  autoStart: boolean;
}

export function emptyDraft(): WizardDraft {
  return {
    name: "",
    seeds: [newSeed()],
    // Structured clone: the defaults object is module-level and shared, so a
    // draft that mutated it in place would poison every later wizard opening.
    policy: {
      ...CRAWL_POLICY_DEFAULTS,
      allowedDomains: [],
      blockedDomains: [],
      includePatterns: [],
      excludePatterns: [],
      assetKinds: [...CRAWL_POLICY_DEFAULTS.assetKinds],
    },
    autoStart: true,
  };
}

/** Seeds that carry text, in submission order, de-duplicated. */
export function usableSeeds(draft: WizardDraft): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of draft.seeds) {
    const value = (s.probe?.url ?? s.raw).trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out.slice(0, CRAWL_LIMITS.seeds.max);
}

/** Hosts the probe resolved — used to prefill the allowlist. */
export function probedHosts(draft: WizardDraft): string[] {
  const out: string[] = [];
  for (const s of draft.seeds) {
    const host = s.probe?.host;
    if (host && !out.includes(host)) out.push(host);
  }
  return out;
}

export interface StepIssue {
  field: string;
  message: string;
}

/**
 * Regex validation mirroring `models.py::_validate_regex_list`.
 *
 * The nested-quantifier check is the same cheap 90% mitigation the backend
 * applies: `re`/`RegExp` are both backtracking engines, so `(a+)+` against a
 * long non-matching string is a CPU denial of service. Rejecting it here just
 * moves the message from a 422 to the form field.
 */
export function validatePattern(pattern: string): string | null {
  const pat = pattern.trim();
  if (!pat) return null;
  if (pat.length > CRAWL_LIMITS.patterns.length) {
    return `Desen çok uzun (>${CRAWL_LIMITS.patterns.length} karakter).`;
  }
  if (/\([^)]*[+*]\)[+*]/.test(pat)) {
    return "İç içe sınırsız niceleyici — katastrofik geri izleme riski.";
  }
  try {
    new RegExp(pat);
  } catch (err) {
    return `Geçersiz düzenli ifade: ${err instanceof Error ? err.message : "?"}`;
  }
  return null;
}

export function stepIssues(step: WizardStep, draft: WizardDraft): StepIssue[] {
  const issues: StepIssue[] = [];
  const p = draft.policy;

  switch (step) {
    case 1: {
      const seeds = usableSeeds(draft);
      if (seeds.length === 0) {
        issues.push({ field: "seeds", message: "En az bir başlangıç adresi gerekli." });
      }
      // A seed the server already refused (SSRF guard, private range, bad
      // scheme) cannot succeed later, so block here rather than at submit.
      for (const s of draft.seeds) {
        if (s.raw.trim() && s.probe && !s.probe.ok) {
          issues.push({
            field: "seeds",
            message: s.probe.message || `Taranamaz adres: ${s.raw}`,
          });
        }
      }
      break;
    }
    case 2: {
      if (p.scopeMode === "allowlist" && p.allowedDomains.length === 0) {
        issues.push({
          field: "allowedDomains",
          message: "İzin listesi kapsamı için en az bir alan adı gerekli.",
        });
      }
      for (const pat of [...p.includePatterns, ...p.excludePatterns]) {
        const err = validatePattern(pat);
        if (err) issues.push({ field: "patterns", message: `${pat}: ${err}` });
      }
      if (p.maxDepth > 6 && p.maxPages > 2000) {
        issues.push({
          field: "budget",
          message:
            "Derinlik 6+ ve 2.000+ sayfa birlikte çok geniş bir tarama demek; " +
            "önce daha küçük bir bütçeyle deneyin.",
        });
      }
      break;
    }
    case 3: {
      // These two mirror the backend's `_enforce_authorisation_gates`; without
      // them the user only learns at submit time, after filling five steps.
      if (!p.respectRobots && !p.ownerDeclared) {
        issues.push({
          field: "respectRobots",
          message:
            "robots.txt yalnızca alan adı sahipliğini beyan ederseniz devre dışı bırakılabilir.",
        });
      }
      if (p.requestsPerSecond > CRAWL_LIMITS.rpsWithoutOwnership && !p.ownerDeclared) {
        issues.push({
          field: "requestsPerSecond",
          message: `${CRAWL_LIMITS.rpsWithoutOwnership} istek/sn üzeri hız için sahiplik beyanı gerekli.`,
        });
      }
      break;
    }
    case 4: {
      if (p.extractAssets && p.assetKinds.length === 0) {
        issues.push({
          field: "assetKinds",
          message: "Varlık toplama açıkken en az bir varlık türü seçin.",
        });
      }
      if (p.llmExtract && !p.extractSchema) {
        issues.push({
          field: "extractSchema",
          message: "Şemalı çıkarım için bir JSON şeması gerekli.",
        });
      }
      break;
    }
    case 5:
      // The summary step re-runs every earlier gate: a user who edits step 3
      // and jumps forward must not be able to submit an invalid policy.
      for (const s of [1, 2, 3, 4] as WizardStep[]) {
        issues.push(...stepIssues(s, draft));
      }
      break;
  }
  return issues;
}

export function isStepValid(step: WizardStep, draft: WizardDraft): boolean {
  return stepIssues(step, draft).length === 0;
}

/** Any seed still being probed — advancing now would skip its verdict. */
export function isProbing(draft: WizardDraft): boolean {
  return draft.seeds.some((s) => s.checking);
}

/**
 * Worst-case request count, for the summary step's honesty box.
 *
 * `maxPages` alone understates the load: asset downloads are requests too, and
 * on an image-heavy site they dominate. Showing only the page budget is how a
 * user aims a "250 page" crawl and emits several thousand requests.
 */
export function estimateRequests(p: CrawlPolicy): number {
  const perPageAssets = p.extractAssets ? Math.min(20, p.assetKinds.length * 8) : 0;
  return p.maxPages * (1 + perPageAssets);
}

export function estimateMinutes(p: CrawlPolicy): number {
  const requests = estimateRequests(p);
  const perSecond = Math.max(0.1, p.requestsPerSecond);
  return Math.min(p.maxDurationMinutes, Math.ceil(requests / perSecond / 60));
}
